import { Skeleton } from "@/shared/components";

export interface DataTableSkeletonProps {
    rowCount?: number;
    columnCount?: number;
    className?: string;
}

/**
 * A skeleton loading state component for data tables
 * Designed to match the responsive layout of our standard data tables
 */
export function DataTableSkeleton({ rowCount = 8, columnCount = 4, className = "" }: DataTableSkeletonProps) {
    // Create arrays for rendering
    const columns = Array.from({ length: columnCount }).map((_, i) => i);
    const rows = Array.from({ length: rowCount }).map((_, i) => i);

    return (
        <div className={`flex w-full flex-col ${className}`}>
            {/* Table actions */}
            <div className="mb-4 flex items-center justify-between">
                <div className="flex items-center gap-2">
                    <Skeleton className="h-9 w-64" />
                </div>
                <div className="flex items-center gap-2">
                    <Skeleton className="h-9 w-24" />
                    <Skeleton className="h-9 w-24" />
                </div>
            </div>

            {/* Table header */}
            <div className="bg-muted/50 flex items-center rounded-t-md border-b px-4 py-3">
                <div className="mr-2 w-10">
                    <Skeleton className="h-5 w-5" />
                </div>
                <div className="flex flex-1">
                    {columns.map((col, index) => {
                        // Hide some columns on mobile/tablet
                        const hideOnMobile = index >= 2 && index < columnCount - 1;
                        const hideOnTablet = index >= 3 && index < columnCount - 1;

                        return (
                            <div
                                key={col}
                                className={`flex-1 font-medium ${hideOnMobile ? "hidden md:block" : ""} ${hideOnTablet ? "hidden lg:block" : ""}`}
                            >
                                <Skeleton className="h-5 w-3/4" />
                            </div>
                        );
                    })}
                    <div className="w-20">{/* Actions column */}</div>
                </div>
            </div>

            {/* Table rows */}
            {rows.map((row) => (
                <div key={row} className="hover:bg-muted/20 flex items-center border-b px-4 py-3 transition-colors">
                    <div className="mr-2 w-10">
                        <Skeleton className="h-4 w-4" />
                    </div>
                    <div className="flex flex-1 items-center">
                        {columns.map((col, index) => {
                            // Hide some columns on mobile/tablet
                            const hideOnMobile = index >= 2 && index < columnCount - 1;
                            const hideOnTablet = index >= 3 && index < columnCount - 1;

                            // Vary the skeleton width for visual variety
                            const widths = ["w-20", "w-32", "w-24", "w-28", "w-36"];
                            const width = widths[index % widths.length];

                            return (
                                <div
                                    key={col}
                                    className={`flex-1 ${hideOnMobile ? "hidden md:block" : ""} ${hideOnTablet ? "hidden lg:block" : ""}`}
                                >
                                    <Skeleton className={`h-5 ${width}`} />
                                </div>
                            );
                        })}
                        <div className="flex w-20 justify-end gap-2">
                            <Skeleton className="h-8 w-8 rounded-md" />
                            <Skeleton className="h-8 w-8 rounded-md" />
                        </div>
                    </div>
                </div>
            ))}

            {/* Pagination */}
            <div className="mt-4 flex items-center justify-between px-4">
                <div>
                    <Skeleton className="h-5 w-40" />
                </div>
                <div className="flex gap-1">
                    <Skeleton className="h-8 w-8 rounded-md" />
                    <Skeleton className="h-8 w-8 rounded-md" />
                    <Skeleton className="h-8 w-8 rounded-md" />
                    <Skeleton className="h-8 w-8 rounded-md" />
                </div>
            </div>
        </div>
    );
}
